import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { useWindowSize } from 'react-use';
import { useConsentUtilities } from './useConsentUtilities';
import { FormModalPopupFields, policy } from './types/ConsentModal.types';
import './ConsentModal.scss';

import CheckBox from '../../@UI/Checkbox/CheckBox';

const ConsentModal: FunctionComponent<FormModalPopupFields> = ({
  policies,
  cancel_button,
  submit_button,
  version,
  origin,
}) => {
  if (!policies) return <></>;

  const { height } = useWindowSize();

  const [consentState, setConsentState] = useState<{
    [type: string]: boolean | null;
  }>({});
  const [invalidOptions, setInvalidOptions] = useState<string[]>([]);

  const setInitialValues = useCallback(() => {
    if (!policies || policies?.length === 0) return;
    let newConsentState = {};
    policies.forEach((pol) => {
      newConsentState[pol.name] = null;
    });
    setConsentState(newConsentState);
  }, []);

  useEffect(() => {
    setInitialValues();
  }, [policies]);

  const {
    handleCloseModal,
    getStringifiedConsent,
    setInputValue,
    fieldsetToInput,
    validateForm,
  } = useConsentUtilities({
    defaultOnCloseBehaviour: () => {
      setInitialValues();
      setInvalidOptions([]);
    },
    origin,
  });

  const handleConsentChange: (e: ChangeEvent<HTMLInputElement>) => void =
    useCallback(({ target: { value, type } }) => {
      if (type === 'checkbox') {
        setConsentState((prevValue) => ({
          ...prevValue,
          [value]: !prevValue[value],
        }));
      } else if (type === 'radio') {
        if (value.startsWith('!')) {
          setConsentState((prevValue) => ({
            ...prevValue,
            [value.slice(1)]: false,
          }));
        } else {
          setConsentState((prevValue) => ({
            ...prevValue,
            [value]: true,
          }));
        }
      }
    }, []);

  const handleSubmit = useCallback(() => {
    const newInvalidOptions = validateForm(policies, consentState);

    if (newInvalidOptions.length > 0) {
      setInvalidOptions(newInvalidOptions);
    } else {
      setInputValue(
        fieldsetToInput(`${origin}_consent_db_values`),
        getStringifiedConsent(consentState)
      );

      setInputValue(fieldsetToInput(`${origin}_consent_db_version`), version);

      setTimeout(() => {
        if(typeof document != 'undefined') {
          const submitButton = document.getElementsByClassName(
            `${origin}_form_submit`
          )[0];
          if (submitButton instanceof HTMLButtonElement) {
            submitButton.click();
          } else {
            submitButton.getElementsByTagName('button')[0].click();
          }
        }
      }, 200);
      setTimeout(() => {
        handleCloseModal(false);
      }, 300);
    }
  }, [consentState, policies]);

  const isInvalid: (pol: policy) => boolean = useCallback(
    (pol) => invalidOptions.includes(pol.name),
    [invalidOptions]
  );

  return (
    <div className="ConsentModal" style={{ height }}>
      <div className="ConsentModal__container">
        <div className="ConsentModal__close_icon">
          <button
            onClick={() => handleCloseModal()}
            className="icon icon-close"
          />
        </div>

        <div className="ConsentModal__inner_container">
          <div className="ConsentModal__red_line" />

          {policies.map((pol, idx) =>
            pol.type === 'checkbox' ? (
              <div
                className={`ConsentModal__checkbox ${
                  isInvalid(pol) ? 'ConsentModal__invalid_text' : ''
                }`}
                key={idx}
              >
                <CheckBox
                  value={pol.name}
                  checked={
                    [null, undefined].includes(consentState[pol.name])
                      ? false
                      : consentState[pol.name]
                  }
                  onChange={handleConsentChange}
                />
                <div>
                  <RichText field={{ value: pol.description }} />
                </div>
              </div>
            ) : (
              <div className="ConsentModal__radio" key={idx}>
                <div className="ConsentModal__radio_options">
                  <div
                    className={
                      isInvalid(pol) ? 'ConsentModal__invalid_text' : ''
                    }
                  >
                    <CheckBox
                      cirle
                      value={pol.name}
                      checked={
                        [null, undefined].includes(consentState[pol.name])
                          ? false
                          : consentState[pol.name]
                      }
                      onChange={handleConsentChange}
                    >
                      {pol.accept_label}
                    </CheckBox>
                  </div>
                  <div
                    className={
                      isInvalid(pol) && !pol.required
                        ? 'ConsentModal__invalid_text'
                        : ''
                    }
                  >
                    <CheckBox
                      cirle
                      value={`!${pol.name}`}
                      checked={
                        [null, undefined].includes(consentState[pol.name])
                          ? false
                          : !consentState[pol.name]
                      }
                      onChange={handleConsentChange}
                    >
                      {pol.deny_label}
                    </CheckBox>
                  </div>
                </div>
                <div className="ConsentModal__radio_policy_desc">
                  <RichText field={{ value: pol.description }} />
                </div>
              </div>
            )
          )}

          <div className="ConsentModal__footer_actions">
            <div className="ConsentModal__footer_actions_first_button">
              <button onClick={handleSubmit}>{submit_button}</button>
            </div>
            <div>
              <button onClick={() => handleCloseModal()}>
                {cancel_button}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
